import { createContext, type ReactNode, useContext } from 'react'
import { createPortal } from 'react-dom'

/**
 * A place for a route to put its own chrome in the shell's top bar.
 *
 * The conversation screen needs to name the agent it is talking to, and that belongs in the
 * header beside the search — not repeated inside the scrolling column. But the header is the
 * shell's and the agent is the route's, so the route sends its element up through a portal
 * rather than the shell reaching down for data it has no other reason to fetch.
 *
 * The portal matters for more than tidiness: content rendered here lands in the DOM *outside*
 * `<main>`, which is where a header belongs and which keeps the conversation column's own
 * assertions about what it contains honest.
 */

const HeaderSlotContext = createContext<HTMLElement | null>(null)

export const HeaderSlotProvider = ({ target, children }: { target: HTMLElement | null; children: ReactNode }) => (
  <HeaderSlotContext.Provider value={target}>{children}</HeaderSlotContext.Provider>
)

/**
 * Renders nothing until the shell has mounted its target. That is one frame on first paint,
 * and the alternative — an imperative ref read during render — is a hydration hazard for a
 * gain nobody can perceive.
 */
export const ShellHeaderSlot = ({ children }: { children: ReactNode }) => {
  const target = useContext(HeaderSlotContext)
  return target ? createPortal(children, target) : null
}
